import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import Nav from "@/components/site/Nav";
import Footer from "@/components/site/Footer";
import { ArrowLeft, CheckCircle2, XCircle, RotateCcw, Mail, Linkedin, Copy } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { usePageMeta } from "@/lib/seo";
import { trackClick } from "@/lib/analytics";
import { PrivacyMention } from "@/components/site/LeadForms";

export default function QuizPage() {
  const { quizId } = useParams();
  const [quiz, setQuiz] = useState(null);
  const [step, setStep] = useState(-1);
  const [answers, setAnswers] = useState([]);
  const [result, setResult] = useState(null);
  const [lead, setLead] = useState({ prenom: "", nom: "", email: "", entreprise: "", fonction: "" });
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  usePageMeta(quiz?.title || "Quiz", quiz?.description);

  useEffect(() => {
    api.get(`/quizzes/${quizId}`).then((r) => setQuiz(r.data)).catch(() => {});
  }, [quizId]);

  const answer = async (idx) => {
    const next = [...answers, idx];
    setAnswers(next);
    if (next.length === quiz.questions.length) {
      const r = await api.post(`/quizzes/${quizId}/submit`, { answers: next });
      setResult(r.data);
    } else {
      setStep(step + 1);
    }
  };

  const restart = () => {
    setStep(0);
    setAnswers([]);
    setResult(null);
    setSent(false);
    setLead({ prenom: "", nom: "", email: "", entreprise: "", fonction: "" });
  };

  const sendResult = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post(`/quizzes/${quizId}/send-result`, { ...lead, answers });
      setSent(true);
      trackClick("quiz_result_email");
      toast.success("Résultat envoyé ! Vérifiez votre boîte mail.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSending(false);
    }
  };

  if (!quiz)
    return (
      <main className="bg-[#0b0f19] min-h-screen">
        <Nav />
        <div className="pt-40 text-center text-slate-500">Chargement…</div>
      </main>
    );

  const shareLink = `${process.env.REACT_APP_BACKEND_URL}/api/share/quiz/${quizId}`;
  const shareText = result
    ? `J'ai évalué mon organisation avec le quiz « ${quiz.title} » proposé par ASAP Technologies × Reqen : ${result.result?.title || `${result.score}/${result.total}`} (score ${result.score}/${result.total}). Faites le test :`
    : "";

  const shareLinkedIn = () => {
    trackClick("quiz_share_linkedin");
    window.open(
      `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareLink)}`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  const copyShareText = async () => {
    try {
      await navigator.clipboard.writeText(`${shareText} ${shareLink}`);
      trackClick("quiz_share_copy");
      toast.success("Texte copié ! Collez-le dans votre publication LinkedIn.");
    } catch {
      toast.error("Copie impossible sur ce navigateur.");
    }
  };

  const q = quiz.questions[step];
  const progress = result ? 100 : Math.round((answers.length / quiz.questions.length) * 100);

  return (
    <main className="bg-[#0b0f19] min-h-screen" data-testid="quiz-page">
      <Nav />
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-24">
        <Link to="/quizz" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-teal-300 transition-colors" data-testid="quiz-back-link">
          <ArrowLeft size={15} /> Retour aux quiz
        </Link>

        <div className="mt-8">
          <div className="text-[11px] font-mono2 uppercase tracking-[0.2em] text-teal-300">
            Quiz · {quiz.category} · {quiz.duration}
          </div>
          <h1 className="mt-3 font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-50" data-testid="quiz-title">
            {quiz.title}
          </h1>
        </div>

        <div className="mt-8 h-1 bg-white/5">
          <div className="h-full grad-line transition-all duration-500" style={{ width: `${progress}%` }} />
        </div>

        <AnimatePresence mode="wait">
          {step === -1 && !result && (
            <motion.div key="intro" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} className="mt-10">
              <p className="text-slate-400 leading-relaxed">{quiz.description}</p>
              <button data-testid="quiz-start-btn" onClick={() => setStep(0)} className="btn-primary mt-8">
                Commencer le quiz
              </button>
            </motion.div>
          )}

          {step >= 0 && !result && q && (
            <motion.div key={step} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.35 }} className="mt-10">
              <div className="font-mono2 text-xs text-slate-500">
                Question {step + 1} / {quiz.questions.length}
              </div>
              <h2 className="mt-3 font-heading text-lg sm:text-xl font-semibold text-slate-100" data-testid="quiz-question">
                {q.question}
              </h2>
              <div className="mt-6 space-y-3">
                {q.options.map((opt, i) => (
                  <button
                    key={i}
                    data-testid={`quiz-option-${i}`}
                    onClick={() => answer(i)}
                    className="w-full text-left border border-white/10 bg-[#131b2c] px-5 py-4 text-sm text-slate-300 hover:border-teal-400/50 hover:bg-[#16233a] transition-colors"
                  >
                    <span className="font-mono2 text-teal-400 mr-3">{String.fromCharCode(65 + i)}</span>
                    {opt}
                  </button>
                ))}
              </div>
            </motion.div>
          )}

          {result && (
            <motion.div key="result" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mt-10" data-testid="quiz-result">
              <div className="border border-teal-400/30 bg-[#10201f] p-8 text-center">
                <div className="font-heading text-5xl font-extrabold grad-text" data-testid="quiz-score">
                  {result.score}/{result.total}
                </div>
                {result.result && (
                  <>
                    <h2 className="mt-4 font-heading text-xl font-semibold text-slate-100">{result.result.title}</h2>
                    <p className="mt-2 text-sm text-slate-400 leading-relaxed">{result.result.message}</p>
                  </>
                )}
                <div className="mt-6 flex flex-wrap justify-center gap-3">
                  <button onClick={restart} className="inline-flex items-center gap-2 px-5 py-2.5 text-sm border border-white/15 text-slate-300 hover:border-teal-400/50 transition-colors" data-testid="quiz-restart-btn">
                    <RotateCcw size={14} /> Recommencer
                  </button>
                  <Link to="/#contact" className="btn-primary !py-2.5" data-testid="quiz-contact-cta">
                    Échanger avec un expert
                  </Link>
                </div>
                <div className="mt-6 pt-6 border-t border-white/10">
                  <p className="text-xs text-slate-500">Partagez votre résultat</p>
                  <div className="mt-3 flex flex-wrap justify-center gap-3">
                    <button onClick={shareLinkedIn} data-testid="quiz-share-linkedin-btn" className="inline-flex items-center gap-2 px-5 py-2.5 text-sm border border-[#0a66c2]/60 text-[#4aa3e6] hover:bg-[#0a66c2]/10 transition-colors">
                      <Linkedin size={15} /> Partager sur LinkedIn
                    </button>
                    <button onClick={copyShareText} data-testid="quiz-share-copy-btn" className="inline-flex items-center gap-2 px-5 py-2.5 text-sm border border-white/15 text-slate-300 hover:border-teal-400/50 transition-colors">
                      <Copy size={14} /> Copier le texte
                    </button>
                  </div>
                </div>
              </div>
              {!sent ? (
                <form onSubmit={sendResult} className="mt-6 border border-white/10 bg-[#111827] p-6" data-testid="quiz-email-form">
                  <div className="flex items-center gap-2 text-slate-100">
                    <Mail size={16} className="text-teal-300" />
                    <h3 className="font-heading text-base font-semibold">Recevoir ce résultat par email</h3>
                  </div>
                  <p className="mt-1 text-xs text-slate-500">Laissez vos coordonnées pour recevoir votre profil et nos recommandations.</p>
                  <div className="mt-4 grid gap-3 sm:grid-cols-2">
                    <input data-testid="quiz-email-prenom" className="input-dark" placeholder="Prénom" value={lead.prenom} onChange={(e) => setLead({ ...lead, prenom: e.target.value })} required />
                    <input data-testid="quiz-email-nom" className="input-dark" placeholder="Nom" value={lead.nom} onChange={(e) => setLead({ ...lead, nom: e.target.value })} required />
                    <input data-testid="quiz-email-email" type="email" className="input-dark" placeholder="E-mail" value={lead.email} onChange={(e) => setLead({ ...lead, email: e.target.value })} required />
                    <input data-testid="quiz-email-entreprise" className="input-dark" placeholder="Entreprise *" value={lead.entreprise} onChange={(e) => setLead({ ...lead, entreprise: e.target.value })} required />
                    <input data-testid="quiz-email-fonction" className="input-dark" placeholder="Fonction *" value={lead.fonction} onChange={(e) => setLead({ ...lead, fonction: e.target.value })} required />
                  </div>
                  <button data-testid="quiz-email-submit" disabled={sending} className="btn-primary mt-4 disabled:opacity-60">
                    {sending ? "Envoi…" : "Recevoir mon résultat"}
                  </button>
                  <PrivacyMention />
                </form>
              ) : (
                <div className="mt-6 border border-teal-400/30 bg-[#10201f] p-6 text-center" data-testid="quiz-email-sent">
                  <CheckCircle2 size={20} className="text-teal-400 mx-auto" />
                  <p className="mt-2 text-sm text-slate-200">Votre résultat vous a été envoyé par email.</p>
                </div>
              )}
              <div className="mt-8 space-y-4">
                {quiz.questions.map((qq, i) => {
                  const c = result.corrections[i];
                  return (
                    <div key={i} className="border border-white/10 bg-[#111827] p-5" data-testid={`quiz-correction-${i}`}>
                      <div className="flex items-start gap-3">
                        {c.correct ? (
                          <CheckCircle2 size={17} className="text-teal-400 mt-0.5 shrink-0" />
                        ) : (
                          <XCircle size={17} className="text-rose-400 mt-0.5 shrink-0" />
                        )}
                        <div>
                          <p className="text-sm text-slate-200">{qq.question}</p>
                          {!c.correct && (
                            <p className="mt-1.5 text-xs text-teal-300">
                              Bonne réponse : {qq.options[c.correct_index]}
                            </p>
                          )}
                          {c.explanation && <p className="mt-1.5 text-xs text-slate-500">{c.explanation}</p>}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      <Footer />
    </main>
  );
}
